import { Alert, Button, Card, EmptyState, LoadingState } from "@swot/base-components";
import { CmtsEnterpriseModal } from "../CmtsEnterpriseModal.js";
import type { QuickAccessUser } from "@services/cmtsLoginApi.js";

export type CmtsQuickAccessModalProps = {
  readonly open: boolean;
  readonly loading: boolean;
  readonly users: readonly QuickAccessUser[];
  readonly securityNotice: string;
  readonly error?: string | undefined;
  readonly onClose: () => void;
  readonly onSelectUser: (user: QuickAccessUser) => void;
};

function displayList(values: readonly string[]): string {
  return values.length ? values.join(", ") : "Not returned by backend";
}

export function CmtsQuickAccessModal(props: CmtsQuickAccessModalProps) {
  return (
    <CmtsEnterpriseModal open={props.open} title="Quick Access Users" size="lg" onClose={props.onClose}>
      <p className="cmts-login-note">{props.securityNotice}</p>
      {props.error ? <Alert title="Quick Access blocked" tone="danger">{props.error}</Alert> : null}
      {props.loading ? <LoadingState title="Loading Quick Access users" message="Requesting eligible users from the CMTS backend." /> : null}
      {!props.loading && props.users.length === 0 ? (
        <EmptyState title="No Quick Access users" message="The backend did not return eligible Quick Access users for this Tenant Code." />
      ) : null}
      <div className="cmts-persona-grid" data-cmts-quick-access-backend-users="true">
        {props.users.map((user) => {
          const selectable = user.isActive && user.canLogin;
          return (
            <Card
              key={user.userId || user.loginId}
              title={user.displayName || user.loginId}
              subtitle={selectable ? "Eligible for Quick Access selection" : "Inactive or login blocked"}
            >
              <p><strong>Login ID:</strong> {user.loginId}</p>
              <p><strong>Email:</strong> {user.email || "Not returned by backend"}</p>
              <p><strong>Roles:</strong> {displayList(user.roles)}</p>
              <p><strong>Permission summary:</strong> {displayList(user.permissionSummary ?? [])}</p>
              <p><strong>Organization scopes:</strong> {displayList(user.orgScopes)}</p>
              <p><strong>Tenant Code:</strong> {user.tenantCode}</p>
              <p><strong>Status:</strong> {selectable ? "Active / can login" : "Blocked"}</p>
              <div className="cmts-persona-card__actions">
                <Button intent="button" onClick={() => props.onSelectUser(user)} disabled={!selectable}>
                  Use this user
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </CmtsEnterpriseModal>
  );
}
